// Runtime client for the Bridge CMS (Cloudflare Worker + D1).
//
// The quiz fetches published bridge pages from the CMS at load time instead of
// reading the bundled BRIDGE_CONTENT. If the CMS is unreachable, everything
// falls back to the bundled content so the funnel never breaks.
import { BridgeScreenConfig, getBundledBridgeContent } from "./bridgeContent";
import { Block, configToBlocks } from "./bridgeBlocks";
import { isLpExperimentVisitor } from "../utils";

const CMS_BASE = (
  process.env.REACT_APP_BRIDGE_CMS_URL || "https://bws-bridge-cms.int-steven.workers.dev"
).replace(/\/$/, "");

/**
 * Which published environment this build reads. The `main` branch deploys to
 * staging.quiz.builtwithscience.com and reads the 'staging' publications; every
 * other host (production, feature previews, localhost) reads 'production'.
 * An explicit REACT_APP_BRIDGE_CMS_ENV overrides the host check.
 */
export function bridgeEnv(): "staging" | "production" {
  const explicit = process.env.REACT_APP_BRIDGE_CMS_ENV;
  if (explicit === "staging" || explicit === "production") return explicit;
  if (typeof window !== "undefined" && /(^|\.)staging\./i.test(window.location.hostname)) return "staging";
  return "production";
}

const PUBLIC_ENDPOINT = `${CMS_BASE}/api/public/bridges?env=${bridgeEnv()}`;

export interface CmsBridge {
  rollout: number; // 0-100: % of this angle's traffic that sees the bridge
  blocks: Block[];
  config: BridgeScreenConfig;
  // Ordered, allow-listed (goals/gender/age) questions to move to the FRONT of the
  // quiz for this angle. Validated server-side (ordered/deduped/≤3). Empty = default flow.
  introSequence?: string[];
  // Bridge header: 'logo' shows the BWS logo on the bridge instead of the progress
  // tracker. The quiz always shows the tracker once it starts. Default 'tracker'.
  headerStyle?: "tracker" | "logo";
  // "Control" variant: this % of the angle's traffic is redirected AWAY from the
  // funnel (to controlUrl, default builtwithscience.com) instead of seeing the
  // bridge or quiz. Buckets: bridge=[0,rollout), control=[100-controlRollout,100).
  controlRollout?: number;
  controlUrl?: string | null;
}

/** Default control-redirect destination when a bridge doesn't set its own. */
export const DEFAULT_CONTROL_URL = "https://builtwithscience.com/";
export type BridgeMap = Record<string, CmsBridge>;

let cache: BridgeMap | null = null;
let inflight: Promise<BridgeMap> | null = null;

function fallbackMap(): BridgeMap {
  const map: BridgeMap = {};
  const content = getBundledBridgeContent();
  for (const key of Object.keys(content)) {
    const config = content[key];
    map[key] = { rollout: 100, blocks: configToBlocks(config), config, introSequence: [], headerStyle: "tracker", controlRollout: 0, controlUrl: null };
  }
  return map;
}

/**
 * Fetch published bridges from the CMS. Resolves to the bundled fallback on failure.
 * Only a SUCCESSFUL response is cached — a transient failure/cancel returns the
 * fallback without caching it, so the next call (or a clean reload) still picks up
 * the live CMS instead of being stuck on bundled content forever.
 */
export function fetchBridges(timeoutMs = 4500): Promise<BridgeMap> {
  if (cache) return Promise.resolve(cache);
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      // Two attempts to ride out a cold start / one transient cancel.
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), timeoutMs);
          const res = await fetch(PUBLIC_ENDPOINT, { signal: controller.signal, credentials: "omit" });
          clearTimeout(timer);
          if (!res.ok) throw new Error(`bridge cms ${res.status}`);
          const data = (await res.json()) as { angles?: BridgeMap };
          cache = data && data.angles && typeof data.angles === "object" ? data.angles : {};
          return cache;
        } catch (err) {
          if (attempt === 1) throw err;
        }
      }
      return fallbackMap();
    } catch {
      return fallbackMap(); // do NOT cache the fallback
    } finally {
      if (!cache) inflight = null; // allow a retry next time if we never succeeded
    }
  })();
  return inflight;
}

/** Synchronously read the loaded map (or the bundled fallback if the fetch hasn't resolved). */
export function getBridges(): BridgeMap {
  return cache || fallbackMap();
}

/** Bridge config for an angle, preferring live CMS content, then bundled content. */
export function getBridgeConfig(angle: string | null | undefined): BridgeScreenConfig | undefined {
  if (!angle) return undefined;
  const fromCms = getBridges()[angle];
  return fromCms ? fromCms.config : getBundledBridgeContent()[angle];
}

/**
 * Bridge content BLOCKS for an angle. Prefers the live CMS blocks; falls back to
 * deriving blocks from the bundled config so the bridge still renders when the
 * CMS is unreachable or the angle is missing. Returns [] if nothing matches.
 */
export function getBridgeBlocks(angle: string | null | undefined): Block[] {
  if (!angle) return [];
  const fromCms = getBridges()[angle];
  if (fromCms && Array.isArray(fromCms.blocks) && fromCms.blocks.length) return fromCms.blocks;
  // CMS unreachable / angle missing / empty blocks -> derive from bundled config.
  return configToBlocks(getBundledBridgeContent()[angle]);
}

/**
 * Ordered intro-sequence question names for an angle (live CMS, then bundled
 * fallback which is always []). Returns [] for null/unknown angles, so the
 * default flow is preserved.
 */
export function getIntroSequence(angle: string | null | undefined): string[] {
  if (!angle) return [];
  return getBridges()[angle]?.introSequence ?? [];
}

/** Bridge header style for an angle: 'logo' shows the BWS logo, else the progress tracker. */
export function getBridgeHeaderStyle(angle: string | null | undefined): "tracker" | "logo" {
  if (!angle) return "tracker";
  return getBridges()[angle]?.headerStyle === "logo" ? "logo" : "tracker";
}

// FNV-1a hash -> deterministic 0-99 bucket. Stable across reloads for the same id+angle.
function bucketOf(id: string, angle: string): number {
  const s = `${id}:${angle}`;
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) % 100;
}

const BUCKET_KEY = "bws_bridge_bucket_id";

/** A stable per-browser id for rollout bucketing; prefers the Statsig stable id if present. */
export function getStableBucketId(preferred?: string | null): string {
  if (preferred) return preferred;
  try {
    let id = localStorage.getItem(BUCKET_KEY);
    if (!id) {
      const c = (typeof crypto !== "undefined" ? crypto : undefined) as Crypto | undefined;
      id = c && typeof c.randomUUID === "function" ? c.randomUUID() : `b_${Math.random().toString(36).slice(2)}`;
      localStorage.setItem(BUCKET_KEY, id);
    }
    return id;
  } catch {
    return "anon";
  }
}

/**
 * Whether this visitor is in the web23 bridge split for `angle` at all: a bridge
 * is published for it and the visitor is not an LP experiment visitor. bws-lp
 * handoffs (e.g. kelly_funnel) skip the split: no bridge, no control redirect,
 * no web23 exposure; they get the quiz, like visitors outside the rollout.
 */
export function isInBridgeSplit(angle: string | null | undefined): boolean {
  return !!angle && !!getBridges()[angle] && !isLpExperimentVisitor();
}

/**
 * Whether this visitor should see the bridge for `angle`.
 * True only when a published bridge exists for the angle and the visitor falls
 * within its rollout %. Visitors outside the rollout go straight into the quiz.
 */
export type BridgeRoute = "bridge" | "control" | "quiz";

/**
 * Which experience this visitor gets for `angle`. Buckets (0-99, stable per
 * id+angle): bridge = [0, rollout); control = [100-controlRollout, 100); quiz =
 * the middle. Unknown/no-angle/out-of-rollout falls through to the quiz.
 *
 * No loop: the control redirect strips utm_angle on the way to the home page, so
 * when the home page links the visitor back to the quiz there's no utm_angle to
 * re-trigger a bridge/control decision (ad -> control -> home -> quiz, then stop).
 */
export function getBridgeRoute(angle: string | null | undefined, id: string): BridgeRoute {
  if (!angle || !isInBridgeSplit(angle)) return "quiz";
  const bridge = getBridges()[angle];
  const rollout = Math.max(0, Math.min(100, bridge.rollout ?? 100));
  const control = Math.max(0, Math.min(100, bridge.controlRollout ?? 0));
  const b = bucketOf(id, angle);
  if (b < rollout) return "bridge";
  if (control > 0 && b >= 100 - control) return "control";
  return "quiz";
}

/** Control-redirect destination for an angle (its own URL, or the default). */
export function getControlUrl(angle: string | null | undefined): string {
  return (angle && getBridges()[angle]?.controlUrl) || DEFAULT_CONTROL_URL;
}

export function shouldShowBridge(angle: string | null | undefined, id: string): boolean {
  return getBridgeRoute(angle, id) === "bridge";
}
